import '../../App.scss';
import { useState, useEffect, useRef } from 'react';
import Modal from '../popup-modal/Modal';
import AssetSOPforms from '../AssetSOPforms';
import { warmupAudio, playClick } from '../../audio/clicks';

/* =========================
   Inline HUD Components
   ========================= */

function HeadingMini({ heading = 0, label = 'N' }) {
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 32,
      padding: '0 8px',
      background: 'rgba(10,10,10,0.8)',
      border: '1px solid #3cff8f',
      borderRadius: 0,
      color: '#3cff8f',
      fontFamily: 'system-ui, sans-serif',
      pointerEvents: 'none',
      gap: 6
    }}>
      <span style={{
        fontVariantNumeric: 'tabular-nums',
        minWidth: '48px',
        textAlign: 'right'
      }}>
        {String(Math.round(heading)).padStart(3, '0')}°
      </span>
      <span style={{
        minWidth: '28px',
        textAlign: 'left',
        display: 'inline-block',
        color: 'red'   // 🔹 all cardinal letters red
      }}>
        {label}
      </span>
    </div>
  );
}

function HorizonLite({ heading = 0, pitch = 0, roll = 0, size = 32 }) {
  const wrap = {
    position: 'relative',
    width: size,
    height: size,
    borderRadius: '50%',
    overflow: 'hidden',
    border: '1px solid #3cff8f',
    background: '#0a0a0a',
    pointerEvents: 'none',
    boxShadow: '0 0 4px #3cff8f66 inset',
    flex: '0 0 auto'
  };
  const sphere = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: size * 2,
    height: size * 2,
    marginLeft: -size,
    marginTop: -size,
    background: `linear-gradient(${90 + pitch}deg, #183a1f 50%, #0a0a0a 50%)`,
    transform: `rotate(${roll}deg)`,
    transition: 'transform 40ms linear, background 40ms linear'
  };
  const crosshair = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: size * 0.6,
    height: 1,
    background: '#00e5ff',
    transform: 'translate(-50%, -50%)',
    boxShadow: '0 0 3px #00e5ff'
  };

  return (
    <div style={wrap} aria-label={`${Math.round(heading)} degrees`}>
      <div style={sphere} />
      <div style={crosshair} />
    </div>
  );
}

/* =========================
   ThemeSelector (updated)
   ========================= */

function ThemeSelector({
  mode,
  setMode,
  viewMode,
  setViewMode,
  setWebUrl,
  containerRef,
  sidebarRef,
  changeMenuModal,
  onStartShare,
  onEndShare,
  shareOn,
  readOnly

}) {
  const [showTags, setShowTags] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalComponent, setModalComponent] = useState(null);
  const [activeBtn, setActiveBtn] = useState(null);   // track last clicked button

  const [heading, setHeading] = useState(0);
  const [pitch, setPitch] = useState(0);
  const [roll, setRoll] = useState(0);

  const lastHeading = useRef(0);
  const lastPitch = useRef(0);
  const lastRoll = useRef(0);

  // Tunables
  const updateInterval = 240;
  const baseSmoothing = 0.65;
  const changeThreshold = 2;
  const lastUpdateTime = useRef(0);

  // 🔊 Unlock audio
  useEffect(() => {
    const unlock = () => {
      warmupAudio();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }, []);

  const withClick = (fn, id) => (e) => {
    if (e?.currentTarget?.disabled || e?.currentTarget?.getAttribute('aria-disabled') === 'true') return;
    playClick();
    setActiveBtn(id);  // mark active
    return fn?.(e);
  };

  // Helpers
  const angleDiff = (a, b) => ((a - b + 540) % 360) - 180;
  const clamp = (v, mn, mx) => Math.max(mn, Math.min(mx, v));
  const lerp = (from, to, t) => from + (to - from) * t;
  const getCardinal = (deg) =>
    ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.round(deg / 45) % 8];

  // Device orientation
  useEffect(() => {
    const handleOrientation = (event) => {
      const now = Date.now();
      if (now - lastUpdateTime.current < updateInterval) return;
      lastUpdateTime.current = now;

      let compass = event.webkitCompassHeading != null ? event.webkitCompassHeading : event.alpha;
      if (compass == null) return;
      if (compass < 0) compass += 360;

      const aDiff = angleDiff(compass, lastHeading.current);
      if (Math.abs(aDiff) >= changeThreshold) {
        const nextH = (lastHeading.current + aDiff * (1 - baseSmoothing) + 360) % 360;
        lastHeading.current = nextH;
        setHeading(nextH);
      }

      const rawPitch = clamp(event.beta ?? 0, -90, 90);
      const rawRoll = clamp(event.gamma ?? 0, -90, 90);

      lastPitch.current = lerp(lastPitch.current, rawPitch, 1 - baseSmoothing);
      lastRoll.current = lerp(lastRoll.current, rawRoll, 1 - baseSmoothing);
      setPitch(lastPitch.current);
      setRoll(lastRoll.current);
    };

    window.addEventListener('deviceorientationabsolute', handleOrientation, true);
    window.addEventListener('deviceorientation', handleOrientation, true);
    return () => {
      window.removeEventListener('deviceorientationabsolute', handleOrientation);
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, []);

  // Modes
  const applyMode = (selectedMode) => {
    setMode(selectedMode);
    const invert = selectedMode === 'dark' ? 'invert(100%)' : selectedMode === 'light' ? 'invert(0%)' : 'none';
    const bgColor = selectedMode === 'dark' ? '#000' : selectedMode === 'light' ? '#fff' : 'transparent';
    if (containerRef?.current) {
      containerRef.current.style.filter = invert;
      containerRef.current.style.backgroundColor = bgColor;
    }
    if (sidebarRef?.current) {
      sidebarRef.current.style.filter = invert;
      sidebarRef.current.style.backgroundColor = bgColor;
    }
  };
  useEffect(() => applyMode('dark'), []);

  const cycleMode = () => {
    if (mode === 'dark') applyMode('light');
    else if (mode === 'light') applyMode('clear');
    else applyMode('dark');
  };

  const toggleTags = () => {
    const next = !showTags;
    setShowTags(next);
    changeMenuModal(next ? 0 : null);
  };

  const openModal = (component) => {
    setModalComponent(component);
    setShowModal(true);
  };

  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

  const TEAMS_URL = "https://teams.microsoft.com/meet/3138806879276?p=bmx8hNMntk0xwbrcxW";
  const handleTeamsCall = () => window.open(TEAMS_URL, "_blank");

  // const CESIUM_URL = "https://3d.energyencyclopedia.com/tokamak/";
  const CESIUM_URL = "https://ion.cesium.com/stories/viewer/?id=231ae960-e920-44fc-ad2a-615eb56dd853";
  const handleToggleCesium = () => {
    if (viewMode === "url") setViewMode("pdf");
    else { setWebUrl(CESIUM_URL); setViewMode("url"); }
    setShowModal(false);
  };

  // Button style (default black, active green)
  const getBtnStyle = (id) => ({
    padding: '6px 12px',
    margin: '4px',
    backgroundColor: activeBtn === id ? 'green' : 'black',
  });

  const rowStyle = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px' };

  return (
    <>
      <div className={mode === 'clear' ? 'clear-overlay' : ''} />
      <div className="mode-toggle-container" style={rowStyle}>
        <HeadingMini heading={heading} label={getCardinal(heading)} />
        <HorizonLite heading={heading} pitch={pitch} roll={roll} />

        <button style={getBtnStyle('mode')} onClick={withClick(cycleMode, 'mode')}>
          {mode.toUpperCase()} MODE
        </button>
        <button style={getBtnStyle('tags')} onClick={withClick(toggleTags, 'tags')}>TAGS</button>

        <Modal show={showModal} onClose={() => setShowModal(false)}>
          {showModal && modalComponent}
        </Modal>

        <button style={getBtnStyle('forms')} onClick={withClick(() => openModal(<AssetSOPforms />, 800, 600), 'forms')}>FORMS</button>

        <button style={getBtnStyle('cesium')} onClick={withClick(handleToggleCesium, 'cesium')}>
          {viewMode === "url" ? "BACK" : "PRE-JOB BRIEF"}
        </button>

{/*  FOR FUTURE TEAM CALL INTEGRATION - USE RETICLE RTC INSTEAD        <button style={getBtnStyle('call')} onClick={withClick(handleTeamsCall, 'call')}>CALL</button> */}

        <button style={{
          ...getBtnStyle('share'),
          backgroundColor: shareOn ? '#c62828' : '#3cff8f',
          color: '#00bef8ff',
          fontWeight: 'bold'
        }}
          onClick={withClick(shareOn ? onEndShare : onStartShare, 'share')}
        >
          {shareOn ? 'END SHARE' : 'SHARE'}
        </button>

        {!isAndroid && (
          <button
            style={getBtnStyle('admin')}
            onClick={withClick(() => window.open('https://pro-visionxr-assetmanager.netlify.app/', '_blank'), 'admin')}
          >
            ADMIN
          </button>
        )}
      </div>
    </>
  );
}

export default ThemeSelector;
